import Localization from '~/client/src/shared/localization/LocalizationManager'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'

import { ISessionGroup, Session } from './types'

const isYesterday = (
  date: Date | number,
  projectDateStore: ProjectDateStore,
): boolean => projectDateStore.isToday(projectDateStore.addDays(date, 1))

const buildGroupLabel = (
  date: Date,
  projectDateStore: ProjectDateStore,
): string => {
  if (projectDateStore.isToday(date)) {
    return `${
      Localization.translator.today
    } · ${projectDateStore.getMonthAndDayToDisplay(date)}`
  }
  if (isYesterday(date, projectDateStore)) {
    return `${
      Localization.translator.yesterday
    } · ${projectDateStore.getMonthAndDayToDisplay(date)}`
  }
  return projectDateStore.getWeekdayMonthAndDayToDisplay(date)
}

export const forEachSessionDaySlice = (
  session: Session,
  effectiveEndedAt: number,
  projectDateStore: ProjectDateStore,
  visit: (dayStart: Date, sliceMs: number) => void,
): void => {
  const startedAt = Number(session.startedAt || 0)
  if (!startedAt) return
  if (effectiveEndedAt <= startedAt) return

  let cursor = startedAt
  while (cursor < effectiveEndedAt) {
    const dayStart = projectDateStore.startOfDay(cursor)
    const nextDayMs = projectDateStore.addDays(dayStart, 1).getTime()
    const sliceEnd = Math.min(effectiveEndedAt, nextDayMs)
    visit(dayStart, sliceEnd - cursor)
    cursor = sliceEnd
  }
}

export const buildSessionGroups = (
  sessions: Session[],
  projectDateStore: ProjectDateStore,
  now: number,
): ISessionGroup[] => {
  const byDay = new Map<number, ISessionGroup>()

  const ensureGroup = (dayStart: Date): ISessionGroup => {
    const dayKey = dayStart.getTime()
    let group = byDay.get(dayKey)
    if (!group) {
      group = {
        dayKey,
        date: dayStart,
        label: buildGroupLabel(dayStart, projectDateStore),
        totalDurationMs: 0,
        sessions: [],
      }
      byDay.set(dayKey, group)
    }
    return group
  }

  sessions.forEach(session => {
    const startedAt = Number(session.startedAt || 0)
    if (!startedAt) return
    const effectiveEndedAt = Number(session.endedAt || 0) || now

    const startDay = projectDateStore.startOfDay(startedAt)
    ensureGroup(startDay).sessions.push(session)

    forEachSessionDaySlice(
      session,
      effectiveEndedAt,
      projectDateStore,
      (dayStart, sliceMs) => {
        ensureGroup(dayStart).totalDurationMs += sliceMs
      },
    )
  })

  byDay.forEach(group => {
    group.sessions.sort(
      (a, b) => Number(b.startedAt || 0) - Number(a.startedAt || 0),
    )
  })

  return Array.from(byDay.values()).sort((a, b) => b.dayKey - a.dayKey)
}
